import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useContent, useLang } from "../i18n/LanguageContext";
import { toast } from "sonner";
import LanguageSwitcher from "../components/LanguageSwitcher";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MARBLE = "https://images.unsplash.com/photo-1690049098415-29f96fca22c0?auto=format&fit=crop&w=1400&q=80";

export default function ForgotPassword() {
  const c = useContent();
  const p = c.portal;
  const { lang } = useLang();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const emailValid = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

  const submit = async (e) => {
    e.preventDefault();
    if (!emailValid(email)) { toast.error(p.errEmailInvalid); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email, lang });
      toast.success(p.forgotOk);
      setSent(true);
    } catch {
      toast.error(p.errGeneric);
    } finally {
      setBusy(false);
    }
  };

  const labelCls = "block text-sm font-medium text-[#16233d] mb-2";
  const inputCls =
    "w-full rounded-lg border border-[#e7e2d8] bg-white px-4 py-3.5 text-sm text-[#16233d] placeholder-[#9aa0ad] focus:outline-none focus:border-[#b08d57] focus:ring-2 focus:ring-[#b08d57]/20 transition";

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      {/* Left: form */}
      <div className="flex flex-col min-h-screen">
        <div className="flex items-center justify-between px-6 lg:px-14 h-20 shrink-0">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md border border-[#e2d3b3] flex items-center justify-center">
              <span className="font-serif-lux text-xl text-[#b08d57] leading-none">B</span>
            </div>
            <div className="leading-tight">
              <div className="font-serif-lux text-lg text-[#16233d] font-semibold">Barlow</div>
              <div className="text-[10px] tracking-[0.18em] text-[#b08d57] uppercase">Consultants SL</div>
            </div>
          </Link>
          <LanguageSwitcher />
        </div>
        <div className="flex-1 flex items-center justify-center px-6 lg:px-14 pb-12">
          <div className="w-full max-w-md">
            <h1 className="font-serif-lux text-4xl text-[#16233d] font-semibold" data-testid="forgot-title">{p.forgotTitle}</h1>
            <p className="mt-2 text-[#5c6577] text-base">{p.forgotSubtitle}</p>
            <form onSubmit={submit} className="mt-8 space-y-5" data-testid="forgot-page-form">
              <div>
                <label className={labelCls}>{p.emailLabel}</label>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} data-testid="forgot-page-email-input" disabled={sent} />
              </div>
              <button type="submit" disabled={busy || sent} className="btn-gold rounded-lg w-full py-4 font-medium disabled:opacity-60" data-testid="forgot-page-submit">
                {p.forgotBtn}
              </button>
            </form>
            {sent && <p className="mt-4 text-sm text-[#3a9d5d]">{p.forgotOk}</p>}
            <div className="mt-6 text-sm">
              <Link to="/login" className="font-medium text-[#b08d57] hover:text-[#8f6f3f] transition-colors" data-testid="forgot-back-login">{p.backToLogin}</Link>
            </div>
          </div>
        </div>
      </div>
      {/* Right: image */}
      <div className="hidden lg:block relative">
        <img src={MARBLE} alt="Barlow Consultants" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#16233d]/75 via-[#16233d]/20 to-transparent" />
        <div className="absolute bottom-14 left-14 right-14">
          <h2 className="font-serif-lux text-4xl leading-tight text-white">{p.authImageTitle}</h2>
        </div>
      </div>
    </div>
  );
}
